/**
 * Per-node actions the node right-click menu borrows from the node itself.
 *
 * Download and Save to Library are node-specific: each node component knows
 * which asset is current (picked variant, panorama source, uploaded file, …)
 * and how to name the file, and owns the save-in-flight state. The context menu
 * is rendered once by the workspace page, so instead of re-deriving any of that
 * it asks the node: node components register a capability here while mounted,
 * and the menu shows an item only when at least one target node registered it.
 *
 * A node can be mounted twice at once (the canvas instance stays mounted, hidden,
 * while list view renders the same node in its detail pane), so registrations
 * stack per node+capability and the most recent live one answers.
 */

import { useEffect, useRef, useSyncExternalStore } from 'react'

export type NodeContextCapability = 'download' | 'saveToLibrary'

export interface NodeContextAction {
  run: () => void
  /** False while the node has the capability but cannot act now (no output yet, save in flight). */
  enabled: boolean
}

interface Registration {
  token: number
  action: NodeContextAction
}

const registrations = new Map<string, Registration[]>()
const listeners = new Set<() => void>()
let version = 0
let nextToken = 1

function keyOf(nodeId: string, capability: NodeContextCapability): string {
  return `${nodeId}\u0000${capability}`
}

function notify(): void {
  version += 1
  listeners.forEach((listener) => listener())
}

/** Register `action` for a node; returns the unregister function. */
export function registerNodeContextAction(
  nodeId: string,
  capability: NodeContextCapability,
  action: NodeContextAction,
): () => void {
  const key = keyOf(nodeId, capability)
  const token = nextToken++
  const stack = registrations.get(key) ?? []
  stack.push({ token, action })
  registrations.set(key, stack)
  notify()
  return () => {
    const current = registrations.get(key)
    if (!current) return
    const remaining = current.filter((entry) => entry.token !== token)
    if (remaining.length > 0) registrations.set(key, remaining)
    else registrations.delete(key)
    notify()
  }
}

export function getNodeContextAction(
  nodeId: string,
  capability: NodeContextCapability,
): NodeContextAction | undefined {
  const stack = registrations.get(keyOf(nodeId, capability))
  return stack?.[stack.length - 1]?.action
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Re-render when any registration changes, so an open menu picks up a node that
 * mounts after the right-click (list view mounts the detail pane on selection).
 */
export function useNodeContextActionsVersion(): number {
  return useSyncExternalStore(subscribe, () => version, () => version)
}

/**
 * Register a node capability for as long as the calling component is mounted.
 * `run` may change every render; only `enabled` changes re-register.
 */
export function useRegisterNodeContextAction(
  nodeId: string,
  capability: NodeContextCapability,
  run: () => void,
  enabled: boolean,
): void {
  const runRef = useRef(run)
  runRef.current = run
  useEffect(
    () => registerNodeContextAction(nodeId, capability, { run: () => runRef.current(), enabled }),
    [nodeId, capability, enabled],
  )
}

/** @internal test seam */
export function __resetNodeContextActions(): void {
  registrations.clear()
  notify()
}
